import {
    createOrganization,
    IApiAxiosError,
    IIndustry,
    IOrganizationCreate,
} from "@/api";
import { IndustryAutocomplete } from "@/components/shared/IndustryAutocomplete";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import { useState } from "react";
import { useNavigate } from "react-router";

export const CreateOrganizationDialog = ({ open, onClose }: DialogProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [name, setName] = useState("");
    const [industry, setIndustry] = useState<IIndustry | null>(null);

    const { mutate, isPending, error } = useMutation({
        mutationFn: (organization: IOrganizationCreate) =>
            createOrganization(organization),
        onSuccess: (organization) => {
            queryClient.invalidateQueries({
                queryKey: ["admin-organizations"],
            });
            navigate(`/admin/organizations/${organization.id}/details`);
            onClose();
        },
    });

    const axiosError = error as IApiAxiosError | null;

    const handleSave = () => {
        if (!name.trim()) return;

        mutate({
            name: name.trim(),
            industry_id: industry?.id,
        });
    };

    return (
        <Modal
            open={open}
            onClose={() => onClose()}
            title="New Organization"
            subtitle="Enter the organization details below"
            maxWidth="sm"
            isLoading={isPending}
            actions={[
                {
                    label: "Create Organization",
                    onClick: handleSave,
                    disabled: !name.trim() || isPending,
                    color: "primary",
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        color="error"
                        variant="caption"
                        sx={{
                            fontSize: "0.75rem",
                        }}
                    >
                        {axiosError.response?.data.detail ||
                            "An error occurred while creating the organization."}
                    </Typography>
                )
            }
        >
            <Stack gap={3}>
                <TextField
                    label="Organization Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                />

                <IndustryAutocomplete
                    value={industry}
                    onChange={setIndustry}
                    helperText="Optional - Associates this organization with an industry"
                />
            </Stack>
        </Modal>
    );
};
